// ---------------------------------------------------------------------------
// Data / service layer
// ---------------------------------------------------------------------------
// The single boundary between the UI and Firestore. Components must not import
// `firebase/firestore` directly — they consume the hooks in src/hooks/content.js,
// which call the functions below.
//
// Collections (see firestore.rules):
//   blogs         public read where published == true
//   testimonials  public read where published == true
//   programmes    public read where published == true
//   seo           public read, one doc per page path
//   settings      public read (site details, code injection)
//   enquiries     public create only, never publicly readable
//
// SERVICES are deliberately NOT in Firestore. The six service pages define the
// route table, each has bespoke page structure, and they are out of scope for
// admin editing — so they stay code-owned in src/data/services.js.
// ---------------------------------------------------------------------------
import {
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  where,
  orderBy,
  limit as fsLimit,
  addDoc,
  serverTimestamp,
} from "firebase/firestore";

import { db } from "@/lib/firebase";
import { SITE } from "@/data/site";
import { SERVICES, getServiceBySlug } from "@/data/services";

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Attach the document id and convert Firestore Timestamps to ISO strings. */
function normalise(snapshot) {
  const data = snapshot.data() || {};
  const out = { id: snapshot.id, ...data };

  Object.keys(out).forEach((key) => {
    const value = out[key];
    if (value && typeof value.toDate === "function") {
      out[key] = value.toDate().toISOString();
    }
  });

  return out;
}

function mapDocs(querySnapshot) {
  return querySnapshot.docs.map(normalise);
}

// ---------------------------------------------------------------------------
// Services (static)
// ---------------------------------------------------------------------------
export const getServices = async () => SERVICES;
export const getService = async (slug) => getServiceBySlug(slug) || null;

// ---------------------------------------------------------------------------
// Programmes
// ---------------------------------------------------------------------------
export async function getProgrammes() {
  const q = query(
    collection(db, "programmes"),
    where("published", "==", true),
    orderBy("order", "asc")
  );
  return mapDocs(await getDocs(q));
}

// ---------------------------------------------------------------------------
// Testimonials
// ---------------------------------------------------------------------------
export async function getTestimonials() {
  const q = query(
    collection(db, "testimonials"),
    where("published", "==", true),
    orderBy("order", "asc")
  );
  return mapDocs(await getDocs(q));
}

// ---------------------------------------------------------------------------
// Blog
// ---------------------------------------------------------------------------
export async function getBlogPosts() {
  const q = query(
    collection(db, "blogs"),
    where("published", "==", true),
    orderBy("publishedAt", "desc")
  );
  return mapDocs(await getDocs(q));
}

/**
 * Fetch one published article by its slug.
 *
 * Always a query on the slug field, never a direct document read.
 *
 * An earlier version tried `getDoc(doc(db, "blogs", slug))` first, because
 * seeded articles use their slug as the document id. For anything created in
 * the admin panel the id is auto-generated, so that read targeted a document
 * that does not exist — and the read rule dereferences `resource.data`, which
 * on a missing document fails the rule rather than returning "not found". The
 * result was PERMISSION_DENIED, retried by react-query, leaving every
 * admin-authored article stuck on its loading skeleton forever.
 */
export async function getBlogPost(slug) {
  if (!slug) return null;

  const q = query(
    collection(db, "blogs"),
    where("slug", "==", slug),
    where("published", "==", true),
    fsLimit(1)
  );
  const found = await getDocs(q);
  return found.empty ? null : normalise(found.docs[0]);
}

// ---------------------------------------------------------------------------
// SEO
// ---------------------------------------------------------------------------
/**
 * Per-page SEO overrides managed in the admin panel. Returns null when no
 * override exists, in which case the page keeps its hardcoded defaults.
 *
 * Document ids are the page path with slashes replaced by hyphens:
 *   "/"                        -> "home"
 *   "/about"                   -> "about"
 *   "/services/wsh-training"   -> "services-wsh-training"
 */
export function seoDocId(path) {
  if (!path || path === "/") return "home";
  return path.replace(/^\//, "").replace(/\/$/, "").replace(/\//g, "-");
}

export async function getSeo(path) {
  const snapshot = await getDoc(doc(db, "seo", seoDocId(path)));
  return snapshot.exists() ? normalise(snapshot) : null;
}

// ---------------------------------------------------------------------------
// Settings
// ---------------------------------------------------------------------------
/** Site contact details. Falls back to the static SITE object. */
export async function getSettings() {
  const snapshot = await getDoc(doc(db, "settings", "site"));
  return snapshot.exists() ? { ...SITE, ...normalise(snapshot) } : SITE;
}

/** Header/body/footer script slots managed in the admin panel. */
export async function getCodeInjection() {
  const snapshot = await getDoc(doc(db, "settings", "codeInjection"));
  return snapshot.exists()
    ? normalise(snapshot)
    : { header: "", body: "", footer: "" };
}

/** Category vocabularies used by the public filters. */
export async function getTaxonomy() {
  const snapshot = await getDoc(doc(db, "settings", "taxonomy"));
  return snapshot.exists() ? normalise(snapshot) : null;
}

// ---------------------------------------------------------------------------
// Enquiries
// ---------------------------------------------------------------------------
/**
 * Store a website enquiry.
 *
 * Writes straight to Firestore; the create is validated field-by-field by
 * firestore.rules. The onEnquiryCreated Cloud Function then sends the admin
 * notification and the customer confirmation.
 *
 * The server-controlled fields below (status/notes/source/createdAt) are
 * required by the security rules to hold exactly these values, so a submitter
 * cannot forge an enquiry that is already "Closed" or backdated.
 */
export async function submitEnquiry(payload) {
  const enquiry = {
    name: payload.name.trim(),
    company: (payload.company || "").trim(),
    mobile: payload.mobile.trim(),
    email: payload.email.trim().toLowerCase(),
    service: payload.service,
    message: payload.message.trim(),
    consent: true,
    status: "new",
    notes: [],
    source: "website",
    createdAt: serverTimestamp(),
  };

  const ref = await addDoc(collection(db, "enquiries"), enquiry);
  return { success: true, id: ref.id };
}
